import {useEffect, useState} from "react";
import ErrorUtils from "src/utils/ErrorUtils";
import StringUtils from "src/utils/stringUtils";
import DateUtils from "src/utils/dateUtils";
import apiService from '../ApiCallService';
// todo! fix these imports from a common module.
import {
    GetMultiAllocationWorksheetsRequest
} from "src/models/multidimensional-allocation/GetMultiAllocationWorksheetsRequest";
import {
    GetMultiAllocationWorksheetsResponse
} from "src/models/multidimensional-allocation/GetMultiAllocationWorksheetsResponse";
import CreateMultiAllocationWorksheetRequest
    from "src/models/multidimensional-allocation/CreateMultiAllocationWorksheetRequest";
import CreateMultiAllocationWorksheetResponse
    from "src/models/multidimensional-allocation/CreateMultiAllocationWorksheetResponse";
import {
    ListMultiAllocationSheetsResponse, MultiAllocationSheetData
} from "src/models/multidimensional-allocation/MultiAllocationSheetData";
import {
    MultiAllocationSheetDetails
} from "src/models/multidimensional-allocation/MultiAllocationSheetDetails";
import {
    MultiAllocationWorksheetData
} from "src/models/multidimensional-allocation/MultiAllocationWorksheetData";
import {
    GetMultiAllocationWorksheetResponse
} from "src/models/multidimensional-allocation/GetMultiAllocationWorksheetResponse";
import {
    GetMultiAllocationWorksheetDataSourcesResponse,
    CreateMultiAllocationWorksheetDataSourceRequest,
    CreateMultiAllocationWorksheetDataSourceResponse,
    DeleteMultiAllocationWorksheetDataSourceResponse, MultiAllocationDataSource, DataKeys
} from "src/models/multidimensional-allocation/MultiAllocationDataSource";
import {MultiAllocationDataSourceRecord} from "src/models/multidimensional-allocation/MultiAllocationDataSourceRecord";
import { CreateMultiAllocationSheetRequest } from "src/models/multidimensional-allocation/CreateMultiAllocationSheetRequest";
import { CreateMultiAllocationSheetResponse } from "src/models/multidimensional-allocation/CreateMultiAllocationSheetResponse";
import { UpdateMultiAllocationSheetRequest } from "src/models/multidimensional-allocation/UpdateMultiAllocationSheetRequest";
import { UpdateMultiAllocationSheetResponse } from "src/models/multidimensional-allocation/UpdateMultiAllocationSheetResponse";
import CONSTANTS from "src/utils/constants";
import CommonDataSourceOperations from "src/services/common/CommonDataSourceOperations";
import {COADataKey} from "src/models/calculation-builder/PullBalanceRequest";
import {
    GetMultiAllocationWorksheetExecutionStatusResponse,
    MultiAllocationWorksheetExecution
} from "src/models/multidimensional-allocation/MultiAllocationWorksheetExecution";
import {
    ApprovalAction, ApprovalType,
    ProcessApprovalRequest,
    ProcessApprovalResponse
} from "src/models/multidimensional-allocation/ProcessApprovals";
import {
    GetMultiAllocationLinkedEntitiesResponse
} from "src/models/multidimensional-allocation/GetMultiAllocationLinkedEntitiesResponse";
import {
    EntityType
} from "src/models/multidimensional-allocation/EntityType";
import moment from 'moment';

/**
 * Service for managing multidimensional allocation operations
 */
export default class MultidimensionalAllocationService {

    /**
     * Retrieves multidimensional allocation worksheets
     * @param query - Query string to trigger the fetch operation
     * @param request - Optional request parameters including status filter
     * @returns Tuple containing [response data, loading state, error message]
     */
    getMultiAllocationWorksheets(query: string, request?: GetMultiAllocationWorksheetsRequest): [GetMultiAllocationWorksheetsResponse | null, boolean, string] {
        const [result, setResult] = useState(null as GetMultiAllocationWorksheetsResponse | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            const fetchData = async () => {
                try {
                    setLoading(true);
                    const response = await apiService.getMultiAllocationWorksheets(request?.status, request?.groupNumber);
                    const json = await response.json() as any;

                    // Transform the data before setting result
                    const transformedResponse: GetMultiAllocationWorksheetsResponse = {
                        ...json, worksheets: json.worksheets ? this.transformWorksheetData(json.worksheets) : []
                    };

                    setResult(transformedResponse);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            };

            if (!StringUtils.isNullOrEmpty(query)) {
                fetchData();
            }
        }, [query, request]);

        return [result, loading, error];
    }

    /**
     * Creates a new multidimensional allocation worksheet
     * @param request - The worksheet creation request containing worksheet name
     * @returns Tuple containing [response data, loading state, error message]
     */
    createWorksheet(request: CreateMultiAllocationWorksheetRequest | null): [CreateMultiAllocationWorksheetResponse | null, boolean, string] {
        const [result, setResult] = useState(null as CreateMultiAllocationWorksheetResponse | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function createWorksheet() {
                try {
                    setLoading(true);
                    setError('');
                    const response = await apiService.createMultiAllocationWorksheet(request!);
                    const json = await response.json() as CreateMultiAllocationWorksheetResponse;
                    setResult(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }

            if (request != null) {
                createWorksheet();
            }
        }, [request]);

        return [result, loading, error];
    }

    /**
     * Clones an existing multidimensional allocation worksheet
     * @param request - The worksheet clone request containing source worksheet ID, new name, provider company code, and version
     * @returns Tuple containing [response data, loading state, error message]
     */
    cloneWorksheet(request: any | null): [any | null, boolean, string] {
        const [result, setResult] = useState(null as any | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function cloneWorksheet() {
                try {
                    setLoading(true);
                    setError('');
                    const response = await apiService.cloneMultiAllocationWorksheet(request!);
                    const json = await response.json();
                    setResult(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }

            if (request != null) {
                cloneWorksheet();
            }
        }, [request]);

        return [result, loading, error];
    }

    /**
     * Retrieves list of multidimensional allocation sheets
     * @param query - Query string to trigger the fetch operation
     * @param status - Optional status filter for sheets
     * @returns Tuple containing [response data, loading state, error message]
     */
    listMultiAllocationSheets(query: string, status?: string): [ListMultiAllocationSheetsResponse | null, boolean, string] {
        const [result, setResult] = useState(null as ListMultiAllocationSheetsResponse | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            const fetchData = async () => {
                try {
                    setLoading(true);
                    const response = await apiService.getMultiAllocationSheets(status);
                    const json = await response.json() as any;

                    // Transform the data before setting result
                    const transformedResponse: ListMultiAllocationSheetsResponse = {
                        ...json, sheets: json.sheets ? this.transformSheetData(json.sheets) : []
                    };

                    setResult(transformedResponse);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            };

            if (!StringUtils.isNullOrEmpty(query)) {
                fetchData();
            }
        }, [query, status]);

        return [result, loading, error];
    }

    async uploadMultiAllocationSheet(key: string, file: File) {
        const bucket = CONSTANTS.ENVIRONMENT_VARIABLES.MDA_SHEETS_UPLOAD_BUCKET;
        return apiService.uploadToS3(bucket, key, file)
    }

    async createMultiAllocationSheet(request: CreateMultiAllocationSheetRequest): Promise<CreateMultiAllocationSheetResponse> {
        const response = await apiService.createMultiAllocationSheet(request);
        const json = await response.json() as CreateMultiAllocationSheetResponse;

        // Transform the sheet data before returning
        const transformedResponse: CreateMultiAllocationSheetResponse = {
            sheetData: this.transformSingleSheetData(json.sheetData)
        };

        return transformedResponse;
    }

    async updateMultiAllocationSheet(request: UpdateMultiAllocationSheetRequest): Promise<UpdateMultiAllocationSheetResponse> {
        const response = await apiService.updateMultiAllocationSheet(request);
        const json = await response.json() as UpdateMultiAllocationSheetResponse;
        
        // Transform the sheet data before returning
        const transformedResponse: UpdateMultiAllocationSheetResponse = {
            sheetData: this.transformSingleSheetDetailsData(json.sheetData)
        };

        return transformedResponse;
    }

    /**
     * Retrieves a single multidimensional allocation sheet
     * @param query - Query string to trigger the fetch operation
     * @param sheetId - The ID of the sheet to retrieve
     * @param version - Optional version number
     * @returns Tuple containing [response data, loading state, error message]
     */
    getMultiAllocationSheet(query: string, sheetId: string, version?: number): [MultiAllocationSheetDetails | null, boolean, string, number[]] {
        const [result, setResult] = useState(null as MultiAllocationSheetDetails | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');
        const [availableVersions, setAvailableVersions] = useState<number[]>([]);

        useEffect(() => {
            const fetchData = async () => {
                try {
                    setLoading(true);
                    const response = await apiService.getMultiAllocationSheet(sheetId, version);
                    const json = await response.json() as any;

                    // Transform the sheet data before setting result
                    const transformedSheet = json.sheetData ? this.transformSingleSheetDetailsData(json.sheetData) : null;
                    setResult(transformedSheet);
                    setAvailableVersions(json.availableVersions)
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            };

            if (!StringUtils.isNullOrEmpty(query) && !StringUtils.isNullOrEmpty(sheetId)) {
                fetchData();
            }
        }, [query, sheetId, version]);

        return [result, loading, error, availableVersions];
    }

    /**
     * Retrieves a specific multidimensional allocation worksheet by ID
     * @param worksheetId - The worksheet ID to fetch
     * @param triggerFetch - Boolean or string to trigger the fetch operation
     * @param worksheetVersion - Optional version number to fetch specific version
     * @returns Tuple containing [all worksheet versions data, loading state, error message]
     */
    getMultiAllocationWorksheet(worksheetId: string, triggerFetch: string, worksheetVersion?: number): [GetMultiAllocationWorksheetResponse | null, boolean, string] {
        const [result, setResult] = useState(null as GetMultiAllocationWorksheetResponse | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchWorksheet() {
                try {
                    setLoading(true);
                    setError('');

                    const response = await apiService.getMultiAllocationWorksheet(worksheetId!, worksheetVersion);
                    const json = await response.json() as GetMultiAllocationWorksheetResponse;

                    setResult(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                    setResult(null);
                } finally {
                    setLoading(false);
                }
            }

            if (worksheetId && !StringUtils.isNullOrEmpty(triggerFetch as string)) {
                fetchWorksheet();
            }
        }, [worksheetId, triggerFetch, worksheetVersion]);

        return [result, loading, error];
    }

    /**
     * Retrieves data sources for a specific multidimensional allocation worksheet
     * @param worksheetId unique id for the worksheet
     * @param worksheetVersion version of the worksheet
     * @param triggerFetch if null fetch call is not triggered.
     * @returns Tuple containing [response data, customCoaReferenceMap, loading state, error message]
     */
    getMultiAllocationWorksheetDataSources(worksheetId?: string, worksheetVersion?: number, triggerFetch?: string):
        [MultiAllocationDataSourceRecord[] | null, Map<string, string> | null, boolean, string] {
        // Start as null (not empty array) so the container can distinguish
        // "haven't fetched yet" from "fetched and got zero records". The
        // container treats `.length === 0` as "auto-add a blank row"; if we
        // pre-seed [] it fires that path before worksheetId/version are ready.
        const [result, setResult] = useState(null as MultiAllocationDataSourceRecord[] | null);
        const [referenceMap, setReferenceMap] = useState(null as Map<string, string> | null);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState('');

        useEffect(() => {
            // `active` drops a slower earlier request that resolves after a newer one, so a stale (possibly
            // empty) result can never replace the rows of the key currently requested.
            let active = true;
            async function fetchDataSources(worksheetId: string, worksheetVersion: number, $this: MultidimensionalAllocationService) {
                try {
                    setLoading(true);
                    setError('');

                    const response = await apiService.getMultiAllocationWorksheetDataSources(worksheetId, worksheetVersion);
                    const json = await response.json() as GetMultiAllocationWorksheetDataSourcesResponse;
                    const {
                        records,
                        customCoaReferenceMap
                    } = $this.transformDataSource(json, worksheetId, worksheetVersion)

                    if (active) {
                        setResult(records);
                        setReferenceMap(customCoaReferenceMap);
                    }
                } catch (ex) {
                    if (active) setError(ErrorUtils.getMessage(ex));
                } finally {
                    if (active) setLoading(false);
                }
            }

            if (worksheetId && worksheetVersion !== undefined && triggerFetch != null) {
                fetchDataSources(worksheetId, worksheetVersion, this);
            }
            return () => {
                active = false;
            };
        }, [worksheetId, worksheetVersion, triggerFetch]);

        return [result, referenceMap, loading, error];
    }

    /**
     * Deletes an existing data source from a multidimensional allocation worksheet
     * @returns Tuple containing [response data, loading state, error message]
     * @param deleteDatasetItemRequest
     */
    deleteMultiAllocationWorksheetDataSource(deleteDatasetItemRequest: {
        parentEntityId: string,
        datasourceId: string
    }):
        [DeleteMultiAllocationWorksheetDataSourceResponse | null, boolean, string] {
        const [result, setResult] = useState(null as DeleteMultiAllocationWorksheetDataSourceResponse | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function deleteDataSource(worksheetId: string, dataSourceId: string) {
                try {
                    setLoading(true);
                    setError('');

                    const response = await apiService.deleteMultiAllocationWorksheetDataSource(worksheetId, dataSourceId);
                    const json = await response.json() as DeleteMultiAllocationWorksheetDataSourceResponse;

                    setResult(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                    setResult(null);
                } finally {
                    setLoading(false);
                }
            }

            if (deleteDatasetItemRequest != null) {
                deleteDataSource(deleteDatasetItemRequest.parentEntityId, deleteDatasetItemRequest.datasourceId);
            }
        }, [deleteDatasetItemRequest]);

        return [result, loading, error];
    }

    /**
     * Creates or updates a data source for a multidimensional allocation worksheet
     * @param dataUpdateOnly - Whether this is a data-only update (no structural changes)
     * @param recordBeingEdited - The data source record to save
     * @param customCoaReference - Map of custom COA references
     * @param recordNeedsSaving - Trigger string to initiate save operation
     * @returns Tuple containing [response data with dataSource field, loading state, error message]
     */
    saveMultiAllocationWorksheetDataSource(dataUpdateOnly: boolean, recordBeingEdited?: MultiAllocationDataSourceRecord, customCoaReference?: Map<string, string>, recordNeedsSaving?: string):
        [CreateMultiAllocationWorksheetDataSourceResponse | null, boolean, string] {
        const [result, setResult] = useState(null as CreateMultiAllocationWorksheetDataSourceResponse | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function createDataSource($this: MultidimensionalAllocationService, record: MultiAllocationDataSourceRecord, customCoaReference: Map<string, string>, dataUpdateOnly: boolean) {
                try {
                    setLoading(true);
                    setError('');
                    const createDataSourceRequest = $this.convertToCreateDataSourceRequest(record, customCoaReference, dataUpdateOnly)
                    const response = recordBeingEdited?.isNewRecord ?
                        await apiService.createMultiAllocationWorksheetDataSource(record.worksheetId, createDataSourceRequest) :
                        await apiService.updateMultiAllocationWorksheetDataSource(record.worksheetId, record.datasourceId, createDataSourceRequest);
                    const json = await response.json() as CreateMultiAllocationWorksheetDataSourceResponse;

                    setResult(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                    setResult(null);
                } finally {
                    setLoading(false);
                }
            }

            if (recordNeedsSaving != null && recordBeingEdited != null && customCoaReference != null) {
                createDataSource(this, recordBeingEdited, customCoaReference, dataUpdateOnly);
            }
        }, [dataUpdateOnly, recordBeingEdited, customCoaReference, recordNeedsSaving]);

        return [result, loading, error];
    }

    /**
     * Executes a specific data source within a multidimensional allocation worksheet.
     * Triggers data source execution and returns the execution result.
     *
     * @param worksheetId - The ID of the worksheet containing the data source
     * @param worksheetVersion - The version of the worksheet to execute
     * @param dataSourceId - The ID of the specific data source to execute
     * @param isExecutionRequired - Trigger string to initiate execution (when not null)
     * @returns Tuple containing [execution result string, loading state, error message]
     */
    executeMultiAllocationWorksheetDataSource(worksheetId?: string, worksheetVersion?: number, dataSourceId?: string, isExecutionRequired?: string):
        [string | null, boolean, string] {
        const [result, setResult] = useState(null as string | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function executeDataSource(worksheetId: string, worksheetVersion: number, dataSourceId: string) {
                try {
                    setLoading(true);
                    setError('');

                    const payload = {
                        "executionSource": CONSTANTS.MDA_EXECUTION_SOURCE.UI_DATASOURCE,
                        "worksheetVersion": worksheetVersion
                    }
                    const response = await apiService.executeMultiAllocationDataSource(worksheetId, dataSourceId, payload);
                    const json = await response.json();

                    setResult(json.executionId);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                    setResult(null);
                } finally {
                    setLoading(false);
                }
            }

            if (isExecutionRequired != null && worksheetId != null && worksheetVersion != null && dataSourceId != null) {
                executeDataSource(worksheetId, worksheetVersion, dataSourceId)
            }
        }, [worksheetId, worksheetVersion, dataSourceId, isExecutionRequired])

        return [result, loading, error]
    }

    /**
     * Executes an entire multidimensional allocation worksheet.
     * Triggers execution of all data sources and calculations within the worksheet.
     *
     * @param worksheetId - The ID of the worksheet to execute
     * @param worksheetVersion - The version of the worksheet to execute
     * @param isExecutionRequired - Trigger string to initiate execution (when not null)
     * @returns Tuple containing [execution result string, loading state, error message]
     */
    executeMultiAllocationWorksheet(worksheetId?: string, worksheetVersion?: number, isExecutionRequired?: string):
        [string | null, boolean, string] {
        const [result, setResult] = useState(null as string | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function executeWorksheet(worksheetId: string, worksheetVersion: number) {
                try {
                    setLoading(true);
                    setError('');

                    const payload = {
                        "worksheetVersion": worksheetVersion
                    }
                    const response = await apiService.executeMultiAllocationWorksheet(worksheetId, CONSTANTS.MDA_EXECUTION_SOURCE.UI, payload);
                    const json = await response.json() as string;

                    setResult(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                    setResult(null);
                } finally {
                    setLoading(false);
                }
            }

            if (isExecutionRequired != null && worksheetId != null && worksheetVersion != null) {
                executeWorksheet(worksheetId, worksheetVersion);
            }
        }, [worksheetId, worksheetVersion, isExecutionRequired])

        return [result, loading, error]
    }

    /**
     * Retrieves the execution status of a multidimensional allocation worksheet.
     * Monitors the progress and status of worksheet execution operations.
     *
     * @param worksheetId - The ID of the worksheet to check execution status for
     * @param executionId - The ID of the specific execution to monitor
     * @param query - to trigger the call
     * @returns Tuple containing [execution status response, loading state, error message]
     */
    getMultiAllocationExecutionStatus(worksheetId?: string, executionId?: string, query?: string):
        [GetMultiAllocationWorksheetExecutionStatusResponse | null, boolean, string] {
        // One state object, so each outcome renders once: React 16 does not batch updates made after an await.
        const [status, setStatus] = useState<{ result: GetMultiAllocationWorksheetExecutionStatusResponse | null, loading: boolean, error: string }>(
            { result: null, loading: false, error: '' });

        useEffect(() => {
            // `active` drops a slower earlier request that resolves after a newer one, so stale presigned
            // links never replace the ones fetched for the latest query key.
            let active = true;
            async function getExecutionStatus(worksheetId: string, executionId?: string) {
                setStatus(previous => ({ ...previous, loading: true, error: '' }));
                try {
                    const response = await apiService.getMultiAllocationExecutionStatus(worksheetId, executionId);
                    const json = await response.json() as GetMultiAllocationWorksheetExecutionStatusResponse;

                    if (active) setStatus({ result: json, loading: false, error: '' });
                } catch (ex) {
                    if (active) setStatus({ result: null, loading: false, error: ErrorUtils.getMessage(ex) });
                }
            }

            if(query != null && worksheetId != null) {
                getExecutionStatus(worksheetId, executionId)
            }
            return () => {
                active = false;
            };
        }, [worksheetId, executionId, query])

        return [status.result, status.loading, status.error];
    }

    /**
     * Fetches one execution's status once, for an action that needs fresh result links: a presigned link
     * expires five minutes after the status call that signed it.
     *
     * @param worksheetId - The worksheet ID
     * @param executionId - The execution whose status rows (and result links) to fetch
     * @returns Promise with the execution status response
     */
    async fetchMultiAllocationExecutionStatus(worksheetId: string, executionId: string): Promise<GetMultiAllocationWorksheetExecutionStatusResponse> {
        const response = await apiService.getMultiAllocationExecutionStatus(worksheetId, executionId);
        return await response.json() as GetMultiAllocationWorksheetExecutionStatusResponse;
    }

    /**
     * Processes approval actions for multidimensional allocation entities.
     * Handles approval workflows for worksheets, sheets, or other allocation entities.
     *
     * @param approvalType - The type of entity being approved (e.g., 'worksheet', 'sheet')
     * @param approvalAction - The approval action to perform (e.g., 'approve', 'reject', 'submit')
     * @param processApprovalRequest - The approval request containing approval data and details
     * @returns Promise containing the approval response data
     */
    async processMultiAllocationApproval(
        approvalType: ApprovalType, 
        approvalAction: ApprovalAction, 
        processApprovalRequest: ProcessApprovalRequest
    ): Promise<ProcessApprovalResponse> {
        const response = await apiService.processMultiAllocationApprovals(approvalType, approvalAction, processApprovalRequest);
        return await response.json() as ProcessApprovalResponse;
    }

    /**
     * Get execution status for multiple datasources
     * @param worksheetId - The worksheet ID
     * @param datasourceRecords
     * @returns Promise with Map of datasourceId to execution status
     */
    async getMultipleExecutionStatuses(worksheetId?: string, datasourceRecords: { datasourceId: string, lastBalancePullId?: string }[] = []): Promise<Map<string, string>> {
        const statusMap = new Map<string, string>();
        
        const promises = datasourceRecords
            .filter(({ lastBalancePullId }) => lastBalancePullId)
            .map(async ({ datasourceId, lastBalancePullId }) => {
                try {
                    const response = await apiService.getMultiAllocationExecutionStatus(worksheetId!, lastBalancePullId!);
                    const data = await response.json();
                    return [datasourceId, data?.datasourceStatuses?.[0]?.executionStatus || "Not Started"] as const;
                } catch (error) {
                    console.error(`Failed to get execution status for datasource ${datasourceId}:`, error);
                    return [datasourceId, "Not Started"] as const;
                }
            });

        const results = await Promise.all(promises);
        results.forEach(([id, status]) => statusMap.set(id, status));

        // Set "Not Started" for records without lastBalancePullId
        datasourceRecords
            .filter(({ lastBalancePullId }) => !lastBalancePullId)
            .forEach(({ datasourceId }) => statusMap.set(datasourceId, "Not Started"));

        return statusMap;
    }

    /**
     * Converts a MultiAllocationDataSourceRecord to API request format
     * @param record - The data source record to convert
     * @param customCoaReference - Map of custom COA references for data key conversion
     * @param dataUpdateOnly - Flag to indicate if this is a data-only update
     * @returns API request object for creating/updating data source
     */
    private convertToCreateDataSourceRequest(record: MultiAllocationDataSourceRecord, customCoaReference: Map<string, string>, dataUpdateOnly: boolean):
        CreateMultiAllocationWorksheetDataSourceRequest {
        const saveDataSourceRequest: CreateMultiAllocationWorksheetDataSourceRequest = {
            worksheetId: record.worksheetId,
            dataSourceType: record.datasource,
            period: record.period,
            description: record.description,
            currency: record.currency,
            allocationSegment: record.allocationSegment,
            allocationSheetId: record.allocationSheetId,
            allocationSheetVersion: record.allocationSheetVersion,
            worksheetName: record.worksheetName
        }

        if (record.customPeriod != null) {
            if (record.customPeriod.indexOf('/') > 0) {
                const customPeriodArr = record.customPeriod.split('-').map(x => x.replace('/', '-'));
                saveDataSourceRequest.startPeriod = customPeriodArr[0].trim();
                saveDataSourceRequest.endPeriod = customPeriodArr[1].trim();
            } else {
                saveDataSourceRequest.startPeriod = record.customPeriod;
                saveDataSourceRequest.endPeriod = record.customPeriod;
            }

            saveDataSourceRequest.period = CONSTANTS.DATA_SOURCE_VALUES.CUSTOM_PERIOD;
        }
        if (record.fxDate != null) {
            saveDataSourceRequest.fxDate = record.fxDate;
        }
        if (record.fxType != null) {
            saveDataSourceRequest.fxType = record.fxType;
        }
        if (record.fxRate != null) {
            saveDataSourceRequest.fxRate = record.fxRate;
        }
        if (record.datasource == CONSTANTS.DATA_SOURCE_TYPES.GENERAL_LEDGER || record.datasource == CONSTANTS.DATA_SOURCE_TYPES.SECONDARY_LEDGER) {
            const dataKeyInput: any = {};
            dataKeyInput.companies = CommonDataSourceOperations.convertToCOADataKeyList(record.dataKeyInput?.selectedCompanies,
                customCoaReference, undefined);
            dataKeyInput.glAccounts = CommonDataSourceOperations.convertToCOADataKeyList(record.dataKeyInput?.selectedAccounts,
                customCoaReference, undefined);
            record.dataKeyInput?.selectedCOA.forEach((value, key) => {
                const coaSegmentArr = Object.values(CONSTANTS.COA_SEGMENT_MAPPING).filter(x => x.UI == key);
                const coaSegment = coaSegmentArr.length > 0 ? coaSegmentArr[0].WEB_API : '';
                dataKeyInput[coaSegment] = CommonDataSourceOperations.convertToCOADataKeyList(value, customCoaReference, undefined);
            });
            saveDataSourceRequest.dataKeys = dataKeyInput;
        }

        if (dataUpdateOnly) {
            saveDataSourceRequest.dataUpdateOnly = true;
        }

        return saveDataSourceRequest;
    }

    /**
     * Transforms API response data sources to UI record format
     * @param getDataSourcesResponse - API response containing data sources array
     * @param worksheetId - Worksheet ID to associate with records
     * @param worksheetVersion - Worksheet version to associate with records
     * @returns Object containing transformed records array and custom COA reference map
     */
    private transformDataSource(getDataSourcesResponse: GetMultiAllocationWorksheetDataSourcesResponse, worksheetId: string, worksheetVersion: number):
        { records: MultiAllocationDataSourceRecord[], customCoaReferenceMap: Map<string, string> } {
        const dataSources = getDataSourcesResponse.dataSources;
        const records: MultiAllocationDataSourceRecord[] = [];
        const customCoaReferenceMap = new Map<string, string>();

        dataSources.forEach((dataSource: MultiAllocationDataSource) => {
            const record = {} as MultiAllocationDataSourceRecord;

            record.worksheetId = worksheetId;
            record.worksheetVersion = worksheetVersion;
            record.worksheetName = dataSource.worksheetName;
            record.datasourceId = dataSource.datasourceId;
            record.datasource = dataSource.datasourceType;
            record.description = dataSource.description as string;
            record.currency = dataSource.currency as string;
            record.fxDate = dataSource.fxDate as string;
            record.fxRate = dataSource.fxRate as string;
            record.fxType = dataSource.fxType as string;
            record.startPeriod = dataSource.startPeriod as string;
            record.endPeriod = dataSource.endPeriod as string;
            record.createUser = dataSource.createUser as string;
            record.createdDate = dataSource.createdDate as number;
            record.lastModifiedUser = dataSource.lastModifiedUser as string;
            record.lastModifiedDate = dataSource.lastModifiedDate as number;
            record.period = dataSource.period as string;
            record.allocationSegment = dataSource.allocationSegment as string;
            record.allocationSheetId = dataSource.allocationSheetId as string;
            record.allocationSheetVersion = dataSource.allocationSheetVersion as number;
            record.lastBalancePullId = dataSource.lastBalancePullId as string;
            record.lastBalancePullDate = dataSource.lastBalancePullDate as number;
            record.s3PresignUrl = dataSource.s3PresignUrl as string;
            record.totalBalance = dataSource.totalBalance as string;

            if (dataSource.errors) {
                record.errors = new Map(Object.entries(dataSource.errors));
            }
            if (dataSource.startPeriod != null && dataSource.endPeriod != null) {
                record.customPeriod = `${dataSource.startPeriod.replace('-', '/')} - ${dataSource.endPeriod.replace('-', '/')}`;
                record.period = null as unknown as string;
            }
            if (record.datasource === CONSTANTS.DATA_SOURCE_TYPES.GENERAL_LEDGER || record.datasource === CONSTANTS.DATA_SOURCE_TYPES.SECONDARY_LEDGER) {

                let keyCount = 2;
                if (dataSource.dataKeys) {
                    record.dataKeyInput = {
                        selectedCompanies: CommonDataSourceOperations.convertToDataKeyJoinedString(dataSource.dataKeys.companies),
                        selectedAccounts: CommonDataSourceOperations.convertToDataKeyJoinedString(dataSource.dataKeys.glAccounts),
                        selectedCOA: new Map<string, string>()
                    }

                    for (const [key, value] of Object.entries(dataSource.dataKeys)) {
                        if (key != CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.WEB_API && key != CONSTANTS.COA_SEGMENT_MAPPING.GL_ACCOUNT.WEB_API) {
                            const coaSegmentArr = Object.values(CONSTANTS.COA_SEGMENT_MAPPING).filter(x => x.WEB_API == key);
                            const coaSegment = coaSegmentArr.length > 0 ? coaSegmentArr[0].UI : '';
                            record.dataKeyInput.selectedCOA.set(coaSegment, CommonDataSourceOperations.convertToDataKeyJoinedString(value as COADataKey[]));
                            keyCount++;
                        }
                    }
                }
                record.dataKey = keyCount + CONSTANTS.DATA_SOURCE_DATA_KEY_COUNT_SUFFIX;
                CommonDataSourceOperations.updateCustomCoaReferenceMap(dataSource.dataKeys, customCoaReferenceMap);
            }

            records.push(record);
        });

        return {records, customCoaReferenceMap};
    }

    /**
     * Utility function to format timestamp to readable date
     */
    private formatDate(timestamp: number): string {
        if (!timestamp) return '-';
        return new Date(timestamp).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    }

    /**
     * Transform backend worksheet data to display format
     */
    private transformWorksheetData(worksheets: any[]): MultiAllocationWorksheetData[] {
        return worksheets.map(worksheet => ({
            ...worksheet,
            createdOn: this.formatDate(worksheet.createdOn),
            lastUpdatedOn: this.formatDate(worksheet.lastUpdatedOn),
            submitDate: this.formatDate(worksheet.submitDate),
            reviewDate: this.formatDate(worksheet.reviewDate)
        }));
    }

    private transformSingleSheetData(sheet: any): MultiAllocationSheetData {
        return {
            ...sheet,
            lastUpdatedAt: this.formatDate(sheet.lastUpdatedAt),
            createdAt: this.formatDate(sheet.createdAt)
        };
    }

    private transformSheetData(sheets: any[]): MultiAllocationSheetData[] {
        return sheets.map(sheet => ({
            ...sheet,
            lastUpdatedAt: this.formatDate(sheet.lastUpdatedAt),
            createdAt: this.formatDate(sheet.createdAt)
        }));
    }

    /**
     * Transform single backend sheet details data to display format
     */
    private transformSingleSheetDetailsData(sheet: any): MultiAllocationSheetDetails {
        return {
            ...sheet,
            lastUpdatedAt: this.formatDate(sheet.lastUpdatedAt),
            createdAt: this.formatDate(sheet.createdAt)
        };
    }

    /**
     * Get execution group for a worksheet
     */
    getOrAssignWorksheetExecutionGroup(worksheetId: string | undefined, loadTime: string | undefined): [any, boolean, string] {
        const [result, setResult] = useState(null as any);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData(worksheetId: string) {
                try {
                    setLoading(true);
                    const response = await apiService.getWorksheetExecutionGroup(worksheetId);
                    const json = await response.json();
                    const formattedResult = json ? {
                        ...json,
                        lastUpdateDate: json.lastUpdateDate ? DateUtils.formatTimestamp(json.lastUpdateDate) : ''
                    } : json;
                    setError('');
                    setResult(formattedResult);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }
            
            if (worksheetId && loadTime) {
                fetchData(worksheetId);
            }
        }, [worksheetId, loadTime]);

        return [result, loading, error];
    }

    /**
     * Save execution group for a worksheet
     */
    saveWorksheetExecutionGroup(payload: any): [any, boolean, string] {
        const [result, setResult] = useState(null as any);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function postData(worksheetId: string, executionGroup: string) {
                try {
                    setLoading(true);
                    const response = await apiService.saveWorksheetExecutionGroup(worksheetId, executionGroup);
                    const json = await response.json();
                    setError('');
                    setResult(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }
            
            if (payload?.worksheetId && payload?.executionGroup) {
                postData(payload.worksheetId, payload.executionGroup);
            }
        }, [payload]);

        return [result, loading, error];
    }

    /**
     * Retrieves linked entities for a given entity type and entity ID.
     * This method fetches all entities that are linked to the specified entity,
     * such as worksheets linked to a sheet or sheets linked to a worksheet.
     *
     * @param entityType - The type of entity (MULTI_ALLOCATION_WORKSHEET or MULTI_ALLOCATION_SHEET)
     * @param entityId - The ID of the entity to get linked entities for
     * @param triggerFetch - Trigger string to initiate the fetch operation (when not null)
     * @returns Tuple containing [response data with linked entities, loading state, error message]
     */
    getMultiAllocationLinkedEntities(entityType?: EntityType, entityId?: string, triggerFetch?: string):
        [GetMultiAllocationLinkedEntitiesResponse | null, boolean, string] {
        const [result, setResult] = useState(null as GetMultiAllocationLinkedEntitiesResponse | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchLinkedEntities(entityType: EntityType, entityId: string) {
                try {
                    setLoading(true);
                    setError('');

                    const response = await apiService.getMultiAllocationLinkedEntities(entityType, entityId);
                    const json = await response.json() as GetMultiAllocationLinkedEntitiesResponse;

                    setResult(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                    setResult(null);
                } finally {
                    setLoading(false);
                }
            }

            if (!StringUtils.isNullOrEmpty(triggerFetch) && entityId && entityType) {
                fetchLinkedEntities(entityType, entityId);
            }
        }, [entityType, entityId, triggerFetch]);

        return [result, loading, error];
    }

    /**
     * Retrieves execution details for a specific worksheet and period
     * Used for loading historical worksheet data
     * 
     * @param worksheetId - The worksheet ID
     * @param period - The execution period (e.g., "JAN-2024")
     * @returns Tuple containing [execution data, loading state, error message]
     */
    getMultiAllocationWorksheetExecution(worksheetId?: string, period?: string):
        [MultiAllocationWorksheetExecution | null, boolean, string] {
        const [result, setResult] = useState(null as MultiAllocationWorksheetExecution | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData(worksheetId: string, period: string, $this: MultidimensionalAllocationService) {
                try {
                    setLoading(true);
                    const convertedPeriod = $this.convertPeriodFormat(period);
                    const response = await apiService.getMultiAllocationWorksheetExecution(worksheetId, convertedPeriod);
                    const json = await response.json();
                    setError('')
                    setResult(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }
            
            if (worksheetId && period) {
                fetchData(worksheetId, period, this);
            }
        }, [worksheetId, period]);
        
        return [result, loading, error];
    }

    private convertPeriodFormat(displayPeriod: string): string {
        // Ex: Convert from "OCT-2025" to "202510"
        return moment(displayPeriod, 'MMM-YYYY').format('YYYYMM');

    }
}
